import React, { useState } from 'react';

export interface PlayerAdminEntry {
  id: number;
  email: string;
  username: string | null;
  /** True until the person accepts by opening the invite link. */
  pending: boolean;
  inviteUrl: string | null;
}

interface PlayerAdminsProps {
  admins: PlayerAdminEntry[];
  busy: boolean;
  onInvite(email: string): Promise<boolean>;
  onRemove(admin: PlayerAdminEntry): void;
  onCopied(text: string): void;
}

/**
 * The owner's Admins tab: the one place to invite the people who help run the jukebox,
 * such as the manager on duty, and to take them off again. Admins can skip, move, remove
 * and add songs. The house rules, the ticker, earnings and this list stay with the owner.
 */
const PlayerAdmins = ({ admins, busy, onInvite, onRemove, onCopied }: PlayerAdminsProps) => {
  const [email, setEmail] = useState('');
  const [confirmId, setConfirmId] = useState<number>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) {
      return;
    }
    if (await onInvite(email.trim())) {
      setEmail('');
    }
  };

  // Two taps, like Skip, so a stray tap does not lock the manager out mid-shift.
  const remove = (admin: PlayerAdminEntry) => {
    if (confirmId !== admin.id) {
      setConfirmId(admin.id);
      return;
    }
    setConfirmId(null);
    onRemove(admin);
  };

  const copy = async (admin: PlayerAdminEntry) => {
    try {
      await navigator.clipboard.writeText(admin.inviteUrl);
      onCopied(`Invite link for ${admin.email} copied.`);
    } catch (error) {
      window.prompt('Copy this invite link', admin.inviteUrl);
    }
  };

  return (
    <div className="control-rules">
      <p className="control-hint">
        Admins can skip, move, remove and add songs. Only you can change Settings, earnings and this
        list.
      </p>

      <form className="jukebox-search" onSubmit={submit}>
        <input
          type="email"
          inputMode="email"
          autoComplete="off"
          placeholder="Their email address"
          aria-label="Email address to invite"
          value={email}
          disabled={busy}
          onChange={e => setEmail(e.target.value)}
        />
        <button type="submit" disabled={busy || !email.trim()}>
          Invite
        </button>
      </form>

      {admins.length === 0 && <p className="jukebox-empty">No admins yet.</p>}

      <ul className="jukebox-list">
        {admins.map(admin => (
          <li key={admin.id} className="jukebox-row">
            <div className="jukebox-row-eyebrow">{admin.pending ? 'Invited' : 'Admin'}</div>
            <div className="jukebox-row-title">{admin.username || admin.email}</div>
            <div className="jukebox-row-meta">
              {admin.pending ? 'Joins when they open the invite link.' : admin.email}
            </div>
            <div className="jukebox-row-actions">
              {admin.pending && !!admin.inviteUrl && (
                <button type="button" className="jukebox-action" disabled={busy} onClick={() => copy(admin)}>
                  Copy invite link
                </button>
              )}
              <button
                type="button"
                className={confirmId === admin.id ? 'jukebox-action jukebox-action-active' : 'jukebox-action'}
                disabled={busy}
                onClick={() => remove(admin)}
              >
                {confirmId === admin.id ? 'Tap again to remove' : admin.pending ? 'Cancel invite' : 'Remove'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PlayerAdmins;
